export interface TelemetryPage {
  page: string;
  guide?: string;
}
export const TELEMETRY_GUIDES = new Set([
  "record-conduct",
  "browse-conduct",
  "manage-scores",
  "teacher-gradebook",
  "create-poll",
  "invite-poll",
  "edit-poll",
  "poll-summary",
  "poll-responses",
  "close-poll",
  "send-announcement",
  "send-parents",
  "send-staff",
  "personalize-message",
  "sms-preview",
  "sms-approval",
  "message-status",
  "sms-credit",
  "find-person",
  "monitor-students",
  "score-coverage",
  "browse-classrooms",
  "grade-report",
  "classroom-report",
  "classroom-subjects",
  "edit-student",
  "student-report-card",
  "student-charts",
  "parent-contacts",
  "move-student",
  "edit-staff",
  "active-classes",
  "join-class",
  "watch-recording",
  "download-recording",
  "view-profile",
  "change-password",
  "switch-school",
  "daily-absences",
  "app-updates",
  "sign-out",
]);
/** Match known pages and approved secondary views only. IDs, searches, capability codes and fragments are never retained. */
export function resolveTelemetryPage(
  pathname: string,
  search: string,
  roles: readonly string[],
): TelemetryPage | null {
  const path = pathname.replace(/\/$/, "") || "/";
  const params = new URLSearchParams(search);
  const view = params.get("view");
  const page = (code: string): TelemetryPage => ({ page: code });
  if (path === "/") return page("home");
  if (path === "/portals") return page("portals");
  if (path === "/profile") return page("profile");
  const portal = /^\/(admin|teacher|student)(?:\/|$)/.exec(path)?.[1];
  if (
    !portal ||
    !roles.includes(
      portal === "admin"
        ? "Admin"
        : portal === "teacher"
          ? "Teacher"
          : "Student",
    )
  )
    return null;
  if (portal === "teacher" && path === "/teacher/scores/gradebook")
    return page("teacher.grades.gradebook");
  if (
    portal === "teacher" &&
    (path === "/teacher/scores" || path === "/teacher/scores/create")
  )
    return page("teacher.grades");
  if (portal !== "admin") {
    if (path === `/${portal}/live-classes`)
      return page(
        `${portal}.classes.${view === "recordings" ? "recordings" : "active"}`,
      );
    if (path === `/${portal}/join`) return page(`${portal}.join`);
    for (const section of ["homework", "grades", "discipline"])
      if (path === `/${portal}/${section}`) return page(`${portal}.${section}`);
    return null;
  }
  if (
    path === "/admin/monitoring/score-coverage" ||
    path === "/admin/monitoring/score-coverage/print"
  )
    return page("admin.monitoring.score-coverage");
  if (path === "/admin/conduct" && view === "records")
    return page("admin.conduct.records");
  if (path === "/admin/conduct" || path === "/admin/conduct/create")
    return page("admin.conduct");
  const conductMessage =
    /^\/admin\/conduct\/records\/[^/]+\/notify\/(all|primary)(?:\/(recipients|compose|preview|approval))?$/.exec(
      path,
    );
  if (conductMessage)
    return page(
      conductMessage[2] === "recipients"
        ? "admin.messages.recipients"
        : "admin.messages.compose",
    );
  if (path === "/admin/monitoring") return page("admin.monitoring");
  if (path === "/admin/monitoring/review-students")
    return page("admin.monitoring.review-students");
  if (path === "/admin/help") return page("admin.help");
  const guide = /^\/admin\/help\/([a-z-]+)$/.exec(path)?.[1];
  if (guide && TELEMETRY_GUIDES.has(guide))
    return { page: "admin.help.guide", guide };
  if (path === "/admin/recordings")
    return page(
      `admin.classes.${view === "recordings" ? "recordings" : "active"}`,
    );
  if (path === "/admin/join") return page("admin.join");
  if (path === "/admin/absences") return page("admin.absences");
  if (path === "/admin/absences/classrooms")
    return page("admin.absences.classrooms");
  if (path === "/admin/messages")
    return page(
      `admin.messages.${view === "history" ? "history" : "audience"}`,
    );
  // Preview and approval remain part of composition in the existing server telemetry catalog.
  for (const step of ["recipients", "compose", "preview", "approval"])
    if (path === `/admin/messages/${step}`)
      return page(`admin.messages.${step === "recipients" ? step : "compose"}`);
  if (/^\/admin\/messages\/jobs\/[^/]+$/.test(path))
    return page("admin.messages.job");
  if (path === "/admin/people")
    return page(
      `admin.people.${view === "grades" ? "grades" : view === "classrooms" ? "classrooms" : view === "students" ? "students" : "staff"}`,
    );
  if (/^\/admin\/people\/grades\/[^/]+$/.test(path))
    return page("admin.people.grades.ranking");
  if (/^\/admin\/people\/classrooms\/[^/]+$/.test(path))
    return page(
      params.get("tab") === "subjects"
        ? "admin.people.classrooms.subjects"
        : "admin.people.classrooms.ranking",
    );
  for (const kind of ["staff", "students"])
    if (new RegExp(`^/admin/people/${kind}/[^/]+$`).test(path)) {
      // Only the selected tab affects attribution; student IDs and report periods never enter event metadata.
      if (kind === "students" && params.get("tab") === "report-card")
        return page("admin.people.students.report-card");
      // Legacy chart links refer to the same specific chart; later chart tabs get separate page codes.
      if (
        kind === "students" &&
        ["score-trend", "charts"].includes(params.get("tab") ?? "")
      )
        return page("admin.people.students.score-trend");
      return page(`admin.people.${kind}.detail`);
    }
  if (path === "/admin/polls")
    return page(
      `admin.polls.${view === "history" ? "history" : "introduction"}`,
    );
  if (path === "/admin/polls/create") return page("admin.polls.create");
  const poll =
    /^\/admin\/polls\/[^/]+(?:\/(questions|results|edit|edit\/review|send|send\/recipients|send\/compose|send\/preview|send\/approval))?$/.exec(
      path,
    );
  if (poll) {
    const step = poll[1];
    if (!step) return page("admin.polls.detail");
    if (step === "results")
      return page(
        `admin.polls.${view === "participants" ? "responses" : "results"}`,
      );
    if (step === "edit/review") return page("admin.polls.review");
    if (step.startsWith("send"))
      return page(
        `admin.polls.invite.${step === "send/approval" || step === "send/preview" ? "compose" : (step.split("/")[1] ?? "audience")}`,
      );
    return page(`admin.polls.${step}`);
  }
  return null;
}
